import { PLAY_MODE, ENTRY_REVOLVE_INIT_Y_RATE, colorAnimTypeList } from './constants';
import { getAnimDirection, getAnimTypeByPlayMode, getSlideWidthAndHeight, getTnRotate } from './utils';
import {
    PATH_ANIM_TYPES,
} from '../../../slide/domain/ui-model/element-modeles/property/animation/constant';
import { getPointsAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { getAnimFromStyleConverter } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-keyframe-converter/anim-from-style-converter';
import type { ComponentAnimWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-anim-wrapper';
import { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { AnimType, PresetSubtype } from '@tencent/slide-sdk-model';

interface IAnimation {
    [key: string]: number | Array<any> | string | undefined ;
}

let maxWidth = 2000;
let maxHeight = 2000;
let objWidth = 0;
let objHeight = 0;
let objLeft = 0;
let objTop = 0;
let maxY;
let minY;
let maxX;
let minX;
function getFromStyle() {
    const animDic = {
        [AnimType.FlyIn + PresetSubtype.Top]: {
            translateY: -(objHeight + minY),
            translateX: 0,
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.Right]: {
            translateX: maxWidth - minX,
            translateY: 0,
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.TopRight]: {
            translateX: maxWidth - minX,
            translateY: -(objHeight + minY),
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.Bottom]: {
            translateY: maxHeight - minY,
            translateX: 0,
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.BottomRight]: {
            translateX: maxWidth - minX,
            translateY: maxHeight - minY,
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.Left]: {
            translateX: -(objWidth + minX),
            translateY: 0,
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.TopLeft]: {
            translateX: -(objWidth + minX),
            translateY: -(objHeight + minY),
            opacity: 1,
        },
        [AnimType.FlyIn + PresetSubtype.BottomLeft]: {
            translateX: -(objWidth + minX),
            translateY: maxHeight - minY,
            opacity: 1,
        },
        [AnimType.EnterScale + PresetSubtype.EleCenter]: {
            opacity: 0,
            scale: 0,
            scaleX: 0,
            scaleY: 0,
        },
        [AnimType.EnterScale + PresetSubtype.SlideCenter]: {
            translateX: (maxWidth / 2) - objLeft - (objWidth / 2),
            translateY: (maxHeight / 2) - objTop - (objHeight / 2),
            opacity: 0,
            scale: 0,
            scaleX: 0,
            scaleY: 0,
        },
        [AnimType.ExitScale + PresetSubtype.ToEleCenter]: {
            opacity: 1,
            scale: 1,
            scaleX: 1,
            scaleY: 1,
        },
        [AnimType.ExitScale + PresetSubtype.ToSlideCenter]: {
            translateX: 0,
            translateY: 0,
            opacity: 1,
            scale: 1,
            scaleX: 1,
            scaleY: 1,
        },
        [AnimType.EntrBasicZoom + PresetSubtype.EleCenter]: {
            scale: 0,
            scaleX: 0,
            scaleY: 0,
        },
        [AnimType.EntrBasicZoom + PresetSubtype.SlideCenter]: {
            translateX: (maxWidth / 2) - objLeft - (objWidth / 2),
            translateY: (maxHeight / 2) - objTop - (objHeight / 2),
            scale: 0,
            scaleX: 0,
            scaleY: 0,
        },
        [AnimType.EntrBasicZoom + PresetSubtype.InSlightly]: {
            scale: 2 / 3,
            scaleX: 2 / 3,
            scaleY: 2 / 3,
            opacity: 0,
        },
        [AnimType.EntrBasicZoom + PresetSubtype.ToEleCenter]: {
            scale: 4,
            scaleX: 4,
            scaleY: 4,
            opacity: 0,
        },
        [AnimType.EntrBasicZoom + PresetSubtype.OutBottom]: {
            scale: 6,
            scaleX: 6,
            scaleY: 6,
            translateX: (maxWidth / 2) - objLeft - (objWidth / 2),
            translateY: maxHeight + objHeight + objTop,
            opacity: 0,
        },
        [AnimType.EntrBasicZoom + PresetSubtype.OutSlightly]: {
            scale: 4 / 3,
            scaleX: 4 / 3,
            scaleY: 4 / 3,
            opacity: 0,
        },
        [AnimType.EntrStretch + PresetSubtype.Across]: {
            scaleX: 0,
        },
        [AnimType.EntrStretch + PresetSubtype.Top]: {
            scaleY: 0,
            transformOrigin: 'center top',
        },
        [AnimType.EntrStretch + PresetSubtype.Bottom]: {
            scaleY: 0,
            transformOrigin: 'center bottom',
        },
        [AnimType.EntrStretch + PresetSubtype.Left]: {
            scaleX: 0,
            transformOrigin: 'left center',
        },
        [AnimType.EntrStretch + PresetSubtype.Right]: {
            scaleX: 0,
            transformOrigin: 'right center',
        },
        [AnimType.ExitCollapse + PresetSubtype.Across]: {
            opacity: 1,
        },
        [AnimType.ExitCollapse + PresetSubtype.Top]: {
            opacity: 1,
            transformOrigin: 'center top',
        },
        [AnimType.ExitCollapse + PresetSubtype.Bottom]: {
            opacity: 1,
            transformOrigin: 'center bottom',
        },
        [AnimType.ExitCollapse + PresetSubtype.Left]: {
            opacity: 1,
            transformOrigin: 'left center',
        },
        [AnimType.ExitCollapse + PresetSubtype.Right]: {
            opacity: 1,
            transformOrigin: 'right center',
        },
        [AnimType.ExitBasicZoom + PresetSubtype.ToEleCenter]: {
            opacity: 1,
        },
        [AnimType.ExitBasicZoom + PresetSubtype.ToSlideCenter]: {
            opacity: 1,
        },
        [AnimType.ExitBasicZoom + PresetSubtype.OutSlightly]: {
            opacity: 1,
        },
        [AnimType.ExitBasicZoom + PresetSubtype.EleCenter]: {
            opacity: 1,
        },
        [AnimType.ExitBasicZoom + PresetSubtype.ToSlideBottom]: {
            opacity: 1,
        },
        [AnimType.ExitBasicZoom + PresetSubtype.InSlightly]: {
            opacity: 1,
        },
        [AnimType.EntrBasicSwivel + PresetSubtype.Across]: {
            rotateY: -90,
            opacity: 0,
        },
        [AnimType.ExitBasicSwivel + PresetSubtype.Across]: {
            rotateY: 360,
            opacity: 1,
        },
        [AnimType.EntrBasicSwivel + PresetSubtype.Vertical]: {
            rotateX: -90,
            opacity: 0,
        },
        [AnimType.ExitBasicSwivel + PresetSubtype.Vertical]: {
            rotateX: 360,
            opacity: 1,
        },
        /** NOTE:
         * 按照 ooxml 应该是 1.125 个单位，但由于滤镜和路径动画速率曲线区别，
         * 在一个较长的动画时间内会有一些偏移的效果，所以此处换成 1 个单位
         * 后续 fromStyle 和 toStyle 中 CutIn 和 CutOut 同理
         */
        [AnimType.CutIn + PresetSubtype.Top]: {
            translateY: -objHeight,
        },
        [AnimType.CutIn + PresetSubtype.Bottom]: {
            translateY: objHeight,
        },
        [AnimType.CutIn + PresetSubtype.Left]: {
            translateX: -objWidth,
        },
        [AnimType.CutIn + PresetSubtype.Right]: {
            translateX: objWidth,
        },
        [AnimType.CutOut + PresetSubtype.Top]: {
            translateY: 0,
        },
        [AnimType.CutOut + PresetSubtype.Bottom]: {
            translateY: 0,
        },
        [AnimType.CutOut + PresetSubtype.Left]: {
            translateX: 0,
        },
        [AnimType.CutOut + PresetSubtype.Right]: {
            translateX: 0,
        },
    };
    return animDic;
}


function setFromStyle(timeNode: IAnimModel, playMode = PLAY_MODE.play, animTarget: ComponentAnimWrapper) {
    const { slideWidth, slideHeight } = getSlideWidthAndHeight(timeNode, playMode);
    const animType = getAnimTypeByPlayMode(timeNode, playMode);

    if (colorAnimTypeList.includes(animType)) {
        const toStyle = timeNode.getAnimModelToStyle();
        if (!toStyle) return {};
        const fromStyle = getAnimFromStyleConverter().convertAnimFromStyle(Object.keys(toStyle), animTarget);
        return fromStyle ?? {};
    }

    const animDirection = getAnimDirection(timeNode, playMode);
    if (slideWidth && slideHeight) {
        maxWidth = slideWidth;
        maxHeight = slideHeight;
    }
    objWidth = timeNode.elementModel.props.style.width;
    objHeight = timeNode.elementModel.props.style.height;
    objLeft = timeNode.elementModel.props.style.left;
    objTop = timeNode.elementModel.props.style.top;
    const objRotate = getTnRotate(timeNode);
    if (objRotate) {
        const fourPoint = getPointsAfterRotate({
            left: objLeft,
            top: objTop,
            width: objWidth,
            height: objHeight,
        }, objRotate);
        fourPoint.sort((a, b) => a.x - b.x);
        maxX = fourPoint[3].x;
        minX = fourPoint[0].x;
        fourPoint.sort((a, b) => a.y - b.y);
        maxY = fourPoint[3].y;
        minY = fourPoint[0].y;
        objHeight = maxY - minY;
        objWidth = maxX - minX;
    } else {
        maxX = objLeft + objWidth;
        minX = objLeft;
        maxY = objTop + objHeight;
        minY = objTop;
    }
    const styleMap = {
        [AnimType.FlyOut]: {
            translateX: 0,
            translateY: 0,
            opacity: 1,
        },
        // 浮入浮出
        [AnimType.FloatUpIn]: {
            opacity: 0,
            translateY: 100,
            translateX: 0,
        },
        [AnimType.FloatUpOut]: {
            opacity: 1,
            translateY: 0,
            translateX: 0,
        },
        [AnimType.FloatDownOut]: {
            opacity: 1,
            translateY: 0,
            translateX: 0,
        },
        [AnimType.FloatDownIn]: {
            opacity: 0,
            translateY: -100,
            translateX: 0,
        },
        [AnimType.Plus]: {
            scaleX: 1,
            scaleY: 1,
            opacity: 1,
        },
        [AnimType.Spread]: {
            opacity: 0,
            scaleX: 0.5,
        },
        [AnimType.EnterRotate]: {
            opacity: 0,
            rotateY: 0,
        },
        [AnimType.ExitSpread]: {
            opacity: 1,
            scaleX: 1,
        },
        [AnimType.ExitRotate]: {
            opacity: 1,
            rotateY: 0,
        },
        [AnimType.EntrCenterRevolve]: {
            opacity: 0,
            translateX: 0,
            translateY: ENTRY_REVOLVE_INIT_Y_RATE * slideHeight,
        },
        [AnimType.EntrGrowTurn]: {
            rotate: 90 + objRotate,
            scale: 0,
            scaleX: 0,
            scaleY: 0,
            opacity: 0,
        },
        [AnimType.EntrRiseUp]: {
            translateY: objHeight + slideHeight,
        },
        [AnimType.EntrSpinner]: {
            rotate: 360 + objRotate,
            scale: 0,
            scaleX: 0,
            scaleY: 0,
            opacity: 0,
        },
        [AnimType.ExitCenterRevolve]: {
            opacity: 1,
            translateY: 0,
        },
        [AnimType.ExitShrinkTurn]: {
            rotate: objRotate,
            opacity: 1,
        },
        [AnimType.ExitSinkDown]: {
            opacity: 1,
        },
        [AnimType.ExitSpinner]: {
            rotate: objRotate,
            opacity: 1,
        },
        [AnimType.ExitStretch]: {
            scaleX: 1,
            opacity: 1,
        },
        [AnimType.EntrPinwheel]: {
            scaleX: 0,
            scaleY: 0,
            scale: 0,
            opacity: 0,
            rotate: 720 + objRotate,
        },
        [AnimType.ExitPinwheel]: {
            scaleX: 1,
            scaleY: 1,
            scale: 1,
            opacity: 1,
            rotate: objRotate,
        },
        [AnimType.EntrFloat]: {
            rotate: -90  + objRotate,
            translateX: 0.4 * slideWidth,
            translateY: - 0.4 * slideHeight,
            opacity: 0,
        },
        [AnimType.ExitFloat]: {
            opacity: 1,
            rotate: objRotate,
        },
        [AnimType.EntrBoomerang]: {
            rotate: -90  + objRotate,
            scaleX: 1,
            translateX: 0.4 * slideWidth,
            translateY: - 0.2 * slideHeight,
            opacity: 0,
        },
        [AnimType.ExitBoomerang]: {
            opacity: 1,
            rotate: objRotate,
        },
        [AnimType.EmphTransparency]: {
            opacity: 1,
        },
        [AnimType.EntrCredits]: {
            opacity: 1,
            translateY: maxHeight,
        },
        [AnimType.ExitCredits]: {
            opacity: 1,
            translateY: -maxHeight,
        },
        [AnimType.CurveUp]: {
            opacity: 0,
            scale: 2.5,
            scaleX: 2.5,
            scaleY: 2.5,
        },
        [AnimType.CurveDown]: {
            opacity: 1,
            scale: 1,
            scaleX: 1,
            scaleY: 1,
        },
        [AnimType.SpiralIn]: {
            scaleX: 0,
            scaleY: 0,
            translateX: -objLeft,
            translateY: -objTop + 1,
        },
        [AnimType.SpiralOut]: {
            scaleX: 1,
            scaleY: 1,
            translateX: 0,
            translateY: 0,
        },
        [AnimType.EntrBounce]: {
            scaleY: 1,
            translateX: -0.25 * slideWidth,
            translateY: -1 / 3 * slideHeight,
        },
        [AnimType.ExitBounce]: {
            scaleY: 1,
            translateX: 0,
            translateY: 0,
        },
    };
    let tempStyle: IAnimation = styleMap[animType];
    if (tempStyle) return tempStyle;

    if (Object.values(PATH_ANIM_TYPES).includes(animType)) {
        return {
            translateX: 0,
            translateY: 0,
            opacity: 1,
        };
    }

    const animDic = getFromStyle();
    tempStyle = animDic[animType + animDirection];
    if (tempStyle) return tempStyle;
    return {};
}
export default setFromStyle;
